
import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bot } from 'lucide-react';
import { useAIAssistant } from '@/contexts/ai-assistant/AIAssistantContext';
import ChatTab from './ai-assistant/ChatTab';
import SettingsTab from './ai-assistant/SettingsTab';

// Conditionally import react-router hooks to avoid errors when outside Router context
const navigateImport = () => {
  try {
    // Dynamic import to check if we're in a Router context
    const { useNavigate, useLocation } = require('react-router-dom');
    return { useNavigate, useLocation, hasRouter: true };
  } catch (e) {
    // Return dummy functions if we're not in a Router context
    return { 
      useNavigate: () => (url: string) => console.warn('Navigation not available:', url),
      useLocation: () => ({ pathname: '/' }),
      hasRouter: false
    };
  }
};

const { useNavigate, hasRouter } = navigateImport();

const AIAssistant = () => {
  const { 
    isOpen, 
    setIsOpen, 
    messages, 
    sendMessage, 
    clearMessages, 
    rateResponse,
    verbosityLevel,
    setVerbosityLevel,
    error,
    clearError
  } = useAIAssistant();
  
  const [inputValue, setInputValue] = useState('');
  const [activeTab, setActiveTab] = useState('chat');
  const [isLoading, setIsLoading] = useState(false);
  
  // Only call useNavigate if we're within a Router context
  const navigate = hasRouter ? useNavigate() : null;
  
  const handleSendMessage = async () => {
    if (inputValue.trim()) {
      setIsLoading(true);
      try {
        await sendMessage(inputValue);
        setInputValue('');
      } catch (err) {
        console.error("Error sending message:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  const handleSourceClick = (url: string) => {
    // Navigate programmatically only if we have router access
    if (url.startsWith('/')) {
      if (navigate) {
        navigate(url);
      } else {
        // Fallback for when Router is not available
        console.warn('Router navigation not available for path:', url);
        window.location.href = url;
      }
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="secondary" 
          className="fixed bottom-6 right-12 z-50 rounded-full w-12 h-12 p-0 shadow-lg"
          onClick={() => setIsOpen(true)}
        >
          <Bot className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="sm:max-w-md md:max-w-lg p-0 flex flex-col h-full">
        <Tabs defaultValue="chat" value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
          <SheetHeader className="px-6 pt-6 pb-2">
            <div className="flex items-center">
              <Bot className="h-6 w-6 mr-2 text-primary" />
              <SheetTitle>DirectPress AI Assistant</SheetTitle>
            </div>
            <TabsList className="grid grid-cols-2 mt-2">
              <TabsTrigger value="chat">Chat</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
          </SheetHeader>
          
          <TabsContent value="chat" className="flex-1 flex flex-col h-full">
            <ChatTab 
              messages={messages}
              inputValue={inputValue}
              setInputValue={setInputValue}
              handleSendMessage={handleSendMessage}
              clearMessages={clearMessages}
              rateResponse={rateResponse}
              error={error}
              clearError={clearError}
              isLoading={isLoading}
              onSourceClick={handleSourceClick}
              setActiveTab={setActiveTab}
              isOpen={isOpen}
            />
          </TabsContent>
          
          <TabsContent value="settings">
            <SettingsTab 
              verbosityLevel={verbosityLevel}
              setVerbosityLevel={setVerbosityLevel}
              onBackToChat={() => setActiveTab('chat')}
            />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default AIAssistant;
